/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  DatePicker,
  Row,
  Col,
  Space,
  message,
} from 'antd';
import { DownloadOutlined, SettingOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import InvalidDomainsModal from './InvalidDomainsModal';
import EmailConfigModal from './EmailConfigModal';
import { getScrapedEmails } from '../../../services/ReportsApi';

const { RangePicker } = DatePicker;

export interface DateRange {
  startDate: Dayjs;
  endDate: Dayjs;
}

interface ScrapedEmail {
  id: number;
  recipient: string;
  email: string;
  recivedAt: Date;
  createdAt: Date;
  updatedAt: Date;
}


const ScrapedEmailsView: React.FC = () => {
  const [emails, setEmails] = useState<ScrapedEmail[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: dayjs().subtract(1, 'month'),
    endDate: dayjs()
  });
  const [invalidDomainsModalVisible, setInvalidDomainsModalVisible] = useState(false);
  const [emailConfigModalVisible, setEmailConfigModalVisible] = useState(false);

  const loadEmails = async (page: number = 1) => {
    setLoading(true);
    try {
      const startDate = dateRange?.startDate?.format('YYYY-MM-DD');
      const endDate = dateRange?.endDate?.format('YYYY-MM-DD');
      const result = await getScrapedEmails(page, pagination.pageSize, startDate, endDate);
      setEmails(result.data);
      setPagination(prev => ({
        ...prev,
        current: result.currentPage,
        total: result.total,
      }));
    } catch (error) {
      message.error('Error loading emails');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!dateRange?.startDate || !dateRange?.endDate) {
      message.error('Please select date range');
      return;
    }

    const diffInDays = dateRange?.endDate.diff(dateRange?.startDate, 'day');
    if (diffInDays > 31) {
      message.error('Date range cannot exceed 31 days');
      return;
    }

    setExporting(true);
    try {
      const startDate = dateRange?.startDate.format('YYYY-MM-DD');
      const endDate = dateRange?.endDate.format('YYYY-MM-DD');
      let data: ScrapedEmail[] = [];
      let withData = true;
      while (withData) {
        const request = await getScrapedEmails(1, 50, startDate, endDate);
        data = [...data, ...request?.data]
        withData = request?.data?.length && request.total > data.length;        
      }
      const worksheet = XLSX.utils.json_to_sheet(data.map(email => ({
        Id: email.id,
        Receptor: email.recipient,
        Remitente: email.email,
        'Recibido': dayjs(email.recivedAt).format('DD/MM/YYYY HH:mm'),
      })));
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Scraped Emails');
      XLSX.writeFile(workbook, `scraped-emails-${startDate}-to-${endDate}.xlsx`);
      
      message.success('Export completed successfully');
    } catch (error) {
      message.error('Error exporting emails');
    } finally {
      setExporting(false);
    }
  };

  const handleSearch = () => {
    if (dateRange?.startDate && dateRange?.endDate) {
      const diffInDays = dateRange?.endDate.diff(dateRange?.startDate, 'day');
      if (diffInDays > 31) {
        message.error('El rango no puede exceder los 31 días');
        return;
      }
    }
    loadEmails(1);
  };

  const handleTableChange = (newPagination: any) => {
    loadEmails(newPagination.current);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Receptor',
      dataIndex: 'recipient',
      key: 'recipient',
    },
    {
      title: 'Remitente',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Recibido',
      dataIndex: 'recivedAt',
      key: 'recivedAt',
      render: (date: Date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
    },
  ];

  useEffect(() => {
    loadEmails(1);
  }, []);

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <RangePicker
              style={{ width: '100%' }}
              value={[dateRange.startDate, dateRange.endDate]}
              onChange={(dateRange) => {
                if (dateRange && dateRange[0] && dateRange[1]) {
                  setDateRange({
                    startDate: dateRange[0], 
                    endDate: dateRange[1]
                  });
                }
              }}
              format="DD/MM/YYYY"
            />
          </Col>
          <Col>
            <Space>
              <Button type="primary" onClick={handleSearch} loading={loading}>
                Buscar
              </Button>
              <Button 
                icon={<DownloadOutlined />} 
                onClick={handleExport}
                loading={exporting}
              >
                Descargar
              </Button>
              <Button 
                icon={<SettingOutlined />}
                onClick={() => setInvalidDomainsModalVisible(true)}
              >
                Dominios inválidos
              </Button>
              <Button 
                icon={<SettingOutlined />}
                onClick={() => setEmailConfigModalVisible(true)}
              >
                Configuración
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={emails}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          rowKey="id"
        />
      </Card>

      {invalidDomainsModalVisible && <InvalidDomainsModal
        visible={invalidDomainsModalVisible}
        onClose={() => setInvalidDomainsModalVisible(false)}
      />}

      {emailConfigModalVisible && <EmailConfigModal
        visible={emailConfigModalVisible}
        onClose={() => setEmailConfigModalVisible(false)}
      />}
    </div>
  );
};

export default ScrapedEmailsView;